import { forwardRef } from "react";
import { Accordion } from "radix-ui";
import clsx from "clsx";

import type {
  RAccordionRootProps,
  RAccordionItemProps,
  RAccordionTriggerProps,
  RAccordionContentProps
} from "./types";

// Root component
const Root = forwardRef<HTMLDivElement, RAccordionRootProps>(
  ({ className, type = "single", ...props }, ref) => (
    // @ts-ignore - Radix UI type issues with union props
    <Accordion.Root
      ref={ref}
      className={clsx("w-full", className)}
      type={type}
      {...props}
    />
  )
);
Root.displayName = "RAccordion.Root";

// Item component
const Item = forwardRef<HTMLDivElement, RAccordionItemProps>(
  ({ className, ...props }, ref) => (
    <Accordion.Item
      ref={ref}
      className={clsx("border-b border-gray-200", className)}
      {...props}
    />
  )
);
Item.displayName = "RAccordion.Item";

// Trigger component
const Trigger = forwardRef<HTMLButtonElement, RAccordionTriggerProps>(
  ({ className, children, ...props }, ref) => (
    <Accordion.Header className="flex">
      <Accordion.Trigger
        ref={ref}
        className={clsx(
          "flex flex-1 items-center justify-between py-4 px-0 text-left font-medium transition-all hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
          className
        )}
        {...props}
      >
        {children}
        <span className="ml-2 transition-transform duration-200 group-data-[state=open]:rotate-180">
          ▼
        </span>
      </Accordion.Trigger>
    </Accordion.Header>
  )
);
Trigger.displayName = "RAccordion.Trigger";

// Content component
const Content = forwardRef<HTMLDivElement, RAccordionContentProps>(
  ({ className, forceMount, ...props }, ref) => (
    <Accordion.Content
      ref={ref}
      className={clsx(
        "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        className
      )}
      {...(forceMount ? { forceMount } : {})}
      {...props}
    />
  )
);
Content.displayName = "RAccordion.Content";

// Main component export
const RAccordion = {
  Root,
  Item,
  Trigger,
  Content
};

export default RAccordion;
